import { applyPercentDiscount } from "./product.js";
/**
 * ═══════════════════════════════════════════════════════════
 * El precio que paga CADA QUIEN — una sola regla para pintar y cobrar
 *
 * Nació el 14-sep-2026, cuando Anahí comparó la tienda vieja con la
 * nueva: "no son los mismos precios". No lo eran porque la nueva
 * enseñaba el precio de socio como un aviso y cobraba el público a todo
 * el mundo (`docs/08`, regla 17). En producción la regla siempre fue:
 *
 *   · Anónimo → precio público (el SRP, que en el viejo es `priceSrp*`).
 *   · Con sesión → precio de SOCIO (`memberPrices`; en el viejo,
 *     `prices`). Cualquier sesión: comprar exige entrar.
 *   · Franquiciatario → el de socio menos su descuento. Decisión de Anahí
 *     del 14-sep: **10 % por tener la franquicia**, en lugar del bono
 *     directo que se pagaba a quien la recomendaba (ver
 *     `contracts/commission.ts`).
 *   · Y lo mismo en los PAQUETES (Anahí, 14-sep, segunda decisión): el
 *     franquiciatario paga el paquete con su 10 % menos.
 *   · Sin excepciones (Anahí, 30-sep): ningún producto ni paquete fija
 *     otro %; `franchiseeDiscountPercent` se ignora. Quien está
 *     ADQUIRIENDO la franquicia en ese mismo pedido ya cuenta como
 *     franquiciatario para el paquete que lleva.
 *
 * Vive en el contrato y no en el frontend ni en el backend porque los
 * DOS lo necesitan: la tarjeta lo pinta y `order.service` lo cobra. Dos
 * copias de esta regla es exactamente cómo el socio acabó viendo un
 * precio y pagando otro.
 * ═══════════════════════════════════════════════════════════
 */
/** El descuento general por tener franquicia, en por ciento. */
export const FRANCHISEE_DISCOUNT_PERCENT = 10;
export const ANONYMOUS_VIEWER = Object.freeze({ isSignedIn: false, isFranchisee: false });
/**
 * El porcentaje de franquiciatario de un producto: SIEMPRE el 10 %.
 *
 * Anahí (30-sep-2026): «con la franquicia digital es 10 % de descuento»,
 * en todo el sistema. El campo `franchiseeDiscountPercent` de cada
 * producto se IGNORA a propósito —había un Kit de Inicio con 25 %— y el
 * admin ya no lo pide. Se conserva la firma para no tocar a quien llama.
 */
export function franchiseeDiscountPercentOf(_product) {
    return FRANCHISEE_DISCOUNT_PERCENT;
}
/**
 * El precio de socio en una moneda: el escrito, o el público si no hay.
 * Es la misma base que usa la tienda personalizada (`storePriceOf`).
 */
export function memberPriceCents(product, currency) {
    return product.memberPrices?.[currency] ?? product.prices[currency];
}
/**
 * Lo que este viewer paga por el producto en esa moneda, en centavos.
 * `undefined` si el producto no se vende en esa moneda.
 */
export function viewerPriceCents(product, currency, viewer) {
    if (!viewer.isSignedIn)
        return product.prices[currency];
    const base = memberPriceCents(product, currency);
    if (base === undefined)
        return undefined;
    if (!viewer.isFranchisee)
        return base;
    return applyPercentDiscount(base, franchiseeDiscountPercentOf(product));
}
/** El porcentaje de franquiciatario de un paquete: SIEMPRE el 10 % (ver arriba). */
export function packageFranchiseeDiscountPercentOf(_pkg) {
    return FRANCHISEE_DISCOUNT_PERCENT;
}
/**
 * Lo que este viewer paga por el paquete en esa moneda. Un paquete no
 * tiene precio de socio: su `prices` ya es el precio negociado, así que
 * con o sin sesión es el mismo, y solo la franquicia lo rebaja.
 */
export function viewerPackagePriceCents(pkg, currency, viewer) {
    const base = pkg.prices[currency];
    if (base === undefined)
        return undefined;
    if (!viewer.isFranchisee)
        return base;
    return applyPercentDiscount(base, packageFranchiseeDiscountPercentOf(pkg));
}
//# sourceMappingURL=pricing.js.map